// ============================================================================
// Supabase client — the only place the SDK is constructed.
//
// The key below is public by design. It carries no privileges of its own:
// every table is behind Row Level Security, so what this client can read is
// decided by the database from the signed-in user's JWT, not by the browser.
// See supabase/migrations/0003_rls.sql.
// ============================================================================

import { createClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!url || !key) {
  throw new Error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY. ' +
    'Copy .env.example to .env and fill it in.'
  );
}

export const supabase = createClient(url, key, {
  auth: {
    // Stay signed in. Creators check sales from a phone they close and reopen
    // days later; making them re-request a magic link every visit is the
    // fastest way to lose them. localStorage survives tab closes and reboots
    // (sessionStorage would not), and the SDK renews the hour-long access
    // token from the long-lived refresh token before it lapses.
    //
    // This is the whole client-side story. How long the refresh token itself
    // stays valid is a SERVER setting — Supabase → Authentication → Sessions.
    // See DEPLOY.md "Session length"; nothing here can extend past it.
    persistSession: true,
    autoRefreshToken: true,
    storage: window.localStorage,
    // Magic-link callbacks arrive as URL fragments; let the SDK consume them.
    detectSessionInUrl: true,
    flowType: 'pkce'
  }
});

/**
 * Supabase surfaces DB exceptions (including our own `raise exception`
 * messages from the RPCs) in a few different shapes. Our RPC errors are
 * written to be shown to the user verbatim, so pull the useful string out.
 */
export function errorMessage(error, fallback = 'Something went wrong. Try again.') {
  if (!error) return fallback;
  const raw = error.message || error.error_description || error.details || '';

  // Postgres prefixes RPC exceptions; strip the noise.
  const cleaned = raw
    .replace(/^.*?(?:ERROR|error):\s*/i, '')
    .replace(/\s*CONTEXT:[\s\S]*$/i, '')
    .trim();

  if (!cleaned) return fallback;

  // Don't leak internals at people.
  if (/duplicate key|violates|relation .* does not exist|permission denied/i.test(cleaned)) {
    return fallback;
  }
  return cleaned;
}
